<template>
    <teleport to="head">
        <title>{{ metaInfo.title }}</title>
        <meta name="description" :content="metaInfo.description">
        <meta property="og:title" :content="metaInfo.ogTitle">
        <meta property="og:description" :content="metaInfo.ogDescription">
        <meta property="og:type" :content="metaInfo.ogType">
        <meta property="og:url" :content="metaInfo.ogUrl">
        <meta property="og:image" :content="metaInfo.ogImage">
        <meta name="twitter:card" :content="metaInfo.twitterCard">
        <meta name="twitter:title" :content="metaInfo.twitterTitle">
        <meta name="twitter:description" :content="metaInfo.twitterDescription">
        <meta name="twitter:image" :content="metaInfo.twitterImage">
    </teleport>

    <div
        ref="invitationRoot"
        class="invitation-view-page"
        :class="{ 'has-fixed-funeral-nav': invitation.invitationType === 'F' }"
    >
        <transition name="invite-fade" mode="out-in" appear>
            <div v-if="isLoading" key="invite-loading" class="invite-loading">
                <span class="invite-loading__dot"></span>
            </div>
            <div v-else-if="hasError || isEmpty || !selectedComponent" key="invite-empty" class="invite-empty">
                존재하지 않는 초대장입니다
            </div>
            <div v-else :key="selectedComponentKey" class="invite-content">
                <component
                    :is="selectedComponent"
                    v-bind="selectedProps"
                />
            </div>
        </transition>

        <div class="invite-copy-url-wrap" v-if="selectedComponent && !isLoading && !hasError && !isEmpty">
            <button
                type="button"
                class="invite-copy-url-btn"
                @click="copyCurrentUrl"
            >
                {{ copyButtonText }}
            </button>
        </div>
    </div>
</template>

<script>
import WeddingViewFormType1 from '@/components/invitation/weddingForm/WeddingViewFormType1.vue';
import WeddingViewFormType2 from '@/components/invitation/weddingForm/WeddingViewFormType2.vue';
import WeddingViewFormType3 from '@/components/invitation/weddingForm/WeddingViewFormType3.vue';
import WeddingViewFormType4 from '@/components/invitation/weddingForm/WeddingViewFormType4.vue';
import FuneralViewFormType1 from '@/components/invitation/funeralForm/FuneralViewFormType1.vue';
import FuneralViewFormType2 from '@/components/invitation/funeralForm/FuneralViewFormType2.vue';
import FuneralViewFormType3 from '@/components/invitation/funeralForm/FuneralViewFormType3.vue';
import FuneralViewFormType4 from '@/components/invitation/funeralForm/FuneralViewFormType4.vue';
import { mapActions } from 'vuex';
import { buildFallbackInvitationMeta, buildInvitationMeta } from '@/utility/invitationMeta';
import '@/assets/css/user/invitation-view-page.css';

const invitationModule = 'invitationModule'
const inviteTextTargetSelector = [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'span', 'strong', 'em', 'small', 'b', 'i',
    'li', 'dt', 'dd', 'th', 'td', 'a', 'button', 'label'
].join(', ');

const weddingComponentMap = {
    '1': WeddingViewFormType1,
    '2': WeddingViewFormType2,
    '3': WeddingViewFormType3,
    '4': WeddingViewFormType4,
}

const funeralComponentMap = {
    '1': FuneralViewFormType1,
    '2': FuneralViewFormType2,
    '3': FuneralViewFormType3,
    '4': FuneralViewFormType4,
}

export default {
    data () {
        return {
            invitation: {},
            isLoading: true,
            hasError: false,
            isEmpty: false,
            scrollRevealObserver: null,
            hasScrollStarted: false,
            copyButtonText: 'URL 복사',
            copyTextTimerId: null,
            latestFetchToken: 0,
        };
    },

    props: {
        orderNum: {
            type: Number,
            required: true,
        }
    },

    methods: {
        ...mapActions(invitationModule, ['requestInvitation']),

        getPreviewShareUrl () {
            const basePath = process.env.BASE_URL || '/';
            const normalizedBasePath = basePath.endsWith('/') ? basePath : `${basePath}/`;
            const previewPath = `${normalizedBasePath}invitation/preview/${this.orderNum}`;
            return new URL(previewPath, window.location.origin).href;
        },

        async fetchInvitationDetail (orderNum) {
            const fetchToken = this.latestFetchToken + 1;
            this.latestFetchToken = fetchToken;

            this.isLoading = true;
            this.hasError = false;
            this.isEmpty = false;
            this.invitation = {};

            try {
                const invitation = await this.requestInvitation(orderNum);

                if (this.latestFetchToken !== fetchToken) {
                    return;
                }

                if (!invitation || !invitation.invitationType) {
                    this.isEmpty = true;
                    return;
                }

                this.invitation = invitation;
            } catch (error) {
                if (this.latestFetchToken !== fetchToken) {
                    return;
                }

                this.hasError = true;
            } finally {
                if (this.latestFetchToken !== fetchToken) {
                    return;
                }

                this.isLoading = false;
                this.setupScrollReveal();
            }
        },

        handleScrollStart () {
            if (this.hasScrollStarted) {
                return;
            }

            if (window.scrollY <= 0) {
                return;
            }

            this.hasScrollStarted = true;
            this.setupScrollReveal();
        },

        async copyCurrentUrl () {
            const currentUrl = this.getPreviewShareUrl();
            let isCopied = false;

            try {
                await navigator.clipboard.writeText(currentUrl);
                isCopied = true;
            } catch (error) {
                isCopied = this.fallbackCopyText(currentUrl);
            }

            this.copyButtonText = isCopied ? '복사됨' : '복사 실패';

            if (this.copyTextTimerId) {
                window.clearTimeout(this.copyTextTimerId);
            }

            this.copyTextTimerId = window.setTimeout(() => {
                this.copyButtonText = 'URL 복사';
                this.copyTextTimerId = null;
            }, 1800);
        },

        fallbackCopyText (text) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();

            let result = false;

            try {
                result = document.execCommand('copy');
            } catch (error) {
                result = false;
            }

            document.body.removeChild(textarea);
            return result;
        },

        setupScrollReveal () {
            this.teardownScrollReveal();

            this.$nextTick(() => {
                const rootEl = this.$refs.invitationRoot;

                if (!(rootEl instanceof HTMLElement)) {
                    return;
                }

                const contentRoot = rootEl.querySelector('.invite-content');

                if (!(contentRoot instanceof HTMLElement)) {
                    return;
                }

                const targets = Array.from(contentRoot.querySelectorAll('.section'));

                if (!targets.length) {
                    return;
                }

                targets.forEach((el, index) => {
                    el.classList.add('invite-scroll-item');
                    el.style.setProperty('--invite-delay', `${Math.min(index * 70, 490)}ms`);

                    const textTargets = Array.from(el.querySelectorAll(inviteTextTargetSelector)).filter((node) => {
                        const hasText = !!node.textContent && node.textContent.trim().length > 0;
                        const isMapInnerNode = !!node.closest('#map');
                        return hasText && !isMapInnerNode;
                    });

                    textTargets.forEach((node, textIndex) => {
                        node.classList.add('invite-text-item');
                        node.style.setProperty('--invite-text-delay', `${Math.min(textIndex * 85, 1105)}ms`);
                    });
                });

                if (!this.hasScrollStarted) {
                    return;
                }

                this.scrollRevealObserver = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (!entry.isIntersecting) {
                                return;
                            }

                            entry.target.classList.add('is-visible');
                            this.scrollRevealObserver?.unobserve(entry.target);
                        });
                    },
                    {
                        threshold: 0,
                        rootMargin: '-35% 0px -35% 0px',
                    }
                );

                targets.forEach((el) => this.scrollRevealObserver?.observe(el));
            });
        },

        teardownScrollReveal () {
            if (!this.scrollRevealObserver) {
                return;
            }

            this.scrollRevealObserver.disconnect();
            this.scrollRevealObserver = null;
        },
    },

    components: {
        WeddingViewFormType1,
        WeddingViewFormType2,
        WeddingViewFormType3,
        WeddingViewFormType4,
        FuneralViewFormType1,
        FuneralViewFormType2,
        FuneralViewFormType3,
        FuneralViewFormType4,
    },

    computed: {
        metaInfo () {
            if (this.hasError || this.isEmpty) {
                return buildFallbackInvitationMeta(window.location.href);
            }

            return buildInvitationMeta(this.invitation, window.location.href);
        },

        selectedComponent () {
            const mainDesign = this.invitation.mainDesign;

            if (this.invitation.invitationType === 'W') {
                return weddingComponentMap[mainDesign] || null;
            }

            if (this.invitation.invitationType === 'F') {
                return funeralComponentMap[mainDesign] || null;
            }

            return null;
        },

        selectedComponentKey () {
            return `${this.invitation.invitationType || 'N'}-${this.invitation.mainDesign || '0'}-${this.invitation.invitationSeq || 0}`;
        },

        selectedProps () {
            const commonProps = {
                invitationSeq: this.invitation.invitationSeq,
            };

            if (this.invitation.invitationType === 'W') {
                return {
                    ...commonProps,
                    weddingInfo: this.invitation.weddingInfo,
                };
            }

            if (this.invitation.invitationType === 'F') {
                return {
                    ...commonProps,
                    funeralInfo: this.invitation.funeralInfo,
                };
            }

            return commonProps;
        },
    },

    watch: {
        orderNum: {
            immediate: true,
            handler (value) {
                this.fetchInvitationDetail(value);
            },
        },

        selectedComponentKey () {
            this.setupScrollReveal();
        },
    },

    mounted () {
        window.addEventListener('scroll', this.handleScrollStart, { passive: true });
        this.handleScrollStart();
    },

    beforeUnmount () {
        this.teardownScrollReveal();
        window.removeEventListener('scroll', this.handleScrollStart);

        if (this.copyTextTimerId) {
            window.clearTimeout(this.copyTextTimerId);
            this.copyTextTimerId = null;
        }
    },
}
</script>
